import Logger from '@utils/log'
import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { EXPORT_CELLS } from '@constants'
import { toast } from 'react-toastify'

const log = new Logger('TemplateViewModel')

const startingRowOptions = Array(20).fill(null).map((_, i) => i + 1)
const startingColOptions = EXPORT_CELLS

const useTemplateViewModel = () => {
    
    const refNewPlaceholderKey = useRef()
    const params = useParams()
    
    const [placeholdersInfoModalOpen, setPlaceholdersInfoModalOpen] = useState(false)
    const [fieldsInfoModalOpen, setFieldsInfoModalOpen] = useState(false)
    const [startingRow, setStartingRow] = useState('1')
    const [startingCol, setStartingCol] = useState(startingColOptions[0])
    const [workbookSheet, setWorkbookSheet] = useState('')
    const [templatePlaceholders, setTemplatePlaceholders] = useState([])
    const [newPlaceholderKey, setNewPlaceholderKey] = useState('')
    const [newPlaceholderValue, setNewPlaceholderValue] = useState('')
    const [pendingDeletePlaceholder, setPendingDeletePlaceholder] = useState(null)
    const [pendingTemplateRename, setPendingTemplateRename] = useState(null)
    const [isUpdatingTemplateFields, setIsUpdatingTemplateFields] = useState(false)
    
    const organizations = useWireValue(store.organizations)
    const exportTemplates = useWireValue(store.exportTemplates)
    
    const {
        organization,
        organizationError,
        organizationIsLoading,
        fetchOrganization,
    } = useOutletContext()
    
    const template = useMemo(() => (
        exportTemplates?.find(it => it.id === params.templateId)
    ), [params, exportTemplates])
    
    const isInternalTemplate = useMemo(() => (
        template?.createdById === null ||
        template?.organizationId === null
    ), [template])
    
    const disabledProps = useMemo(() => (
        isInternalTemplate ? {
            disabled: true,
            title: 'Internal Parse templates can\'t be modified',
        } : {}
    ), [isInternalTemplate])
    
    // @todo this is duplicated in the Templates component - should be abstracted somewhere
    const fetchExportTemplates = useCallback(async () => {
        
        try {
            await actions.getExportTemplatesByOrgId(organization.id)
        } catch (e) {
            // @todo show error
            console.error('Templates initial fetch', e)
            toast.error('Failed to fetch export runsheets templates')
        }
        
    }, [organization])
    
    const fetchTemplatePlaceholders = useCallback(async () => {
        
        try {
            
            const res = await actions.getTemplatePlaceholders()
            const values = Object.values(res)
            
            setTemplatePlaceholders(values)
            
            setNewPlaceholderValue(prev => {
                if (prev?.length) return prev
                if (values?.length) return values[0]
            })
            
        } catch (e) {
            
            // @todo show error
            console.error('Template placeholders initial fetch', e)
            
        }
        
    }, [])
    
    const fetchTemplate = useCallback(async () => {
        
        try {
            await actions.getExportTemplateById(organization.id, template.id)
        } catch (e) {
            console.error('Export templates initial fetch', e)
            toast.error('Failed to fetch template')
        }
        
    }, [organization, template])
    
    const onRenameTemplateClick = useCallback(async () => {
        
        if (pendingTemplateRename) {
            
            // No rename if the name hasn't changed
            if (pendingTemplateRename !== template?.name) {
                
                try {
                    
                    await actions.updateExportTemplate(params.orgId, template.id, {
                        name: pendingTemplateRename,
                    })
                    
                    // Save changes
                    toast.success(`Export template renamed "${pendingTemplateRename}"`)
                    
                } catch (e) {
                    
                    console.error('onRenameTemplateClick', e)
                    toast.error('Failed to rename template. Please try again.')
                    
                }
                
            }
            
            setPendingTemplateRename(null)
            
        } else {
            
            setPendingTemplateRename(template?.name ?? '')
            
        }
        
    }, [template, pendingTemplateRename, params])
    
    const onStartingRowChange = useCallback(async e => {
        
        if (e.target.value === startingRow) return
        
        setStartingRow(e.target.value)
        
        try {
            await actions.updateExportTemplate(organization.id, template.id, {
                startingRow: parseInt(e.target.value, 10),
            })
        } catch (e) {
            log.e('onStartingRowChange', e)
            toast.error('Failed to update starting row')
        }
        
    }, [organization, template, startingRow])
    
    const onStartingColChange = useCallback(async e => {
        
        if (e.target.value === startingCol) return
        
        setStartingCol(e.target.value)
        
        try {
            await actions.updateExportTemplate(organization.id, template.id, {
                startingCol: e.target.value,
            })
        } catch (e) {
            log.e('onStartingColChange', e)
            toast.error('Failed to update starting col')
        }
        
    }, [organization, template, startingCol])
    
    const onWorkbookSheetChange = useCallback(async e => {
        
        if (e.target.value === workbookSheet) return
        
        setWorkbookSheet(e.target.value)
        
        try {
            await actions.updateExportTemplate(organization.id, template.id, {
                defaultSheet: e.target.value,
            })
        } catch (e) {
            log.e('onWorkbookSheetChange', e)
            toast.error('Failed to update default sheet')
        }
        
    }, [organization, template, workbookSheet])
    
    const onPlaceholderValueChange = useCallback(e => {
        
        setNewPlaceholderValue(prev => {
            
            // If the key is empty or === the previous value,
            // set the new value as default, for convenience
            if (!newPlaceholderKey?.length || newPlaceholderKey === prev)
                setNewPlaceholderKey(e.target.value)
            
            return e.target.value
            
        })
        
    }, [newPlaceholderKey])
    
    const onAddPlaceholderClick = useCallback(async (forceKey = null, forceValue = null) => {
        
        const key = forceKey || newPlaceholderKey
        const value = forceValue || newPlaceholderValue
        
        if (!key?.length || !value?.length)
            return refNewPlaceholderKey.current?.focus()
        
        if (!organization?.id?.length)
            return console.warn('No org set', organization)
        
        if (!template?.id?.length)
            return console.warn('No export template set', template)
        
        try {
            await actions.createExportTemplatePlaceholder(
                organization.id,
                template.id,
                key,
                value,
            )
            await fetchTemplate()
            setNewPlaceholderKey('')
            setNewPlaceholderValue(templatePlaceholders[0])
        } catch (e) {
            console.error('onAddPlaceholderClick', e)
            toast.error('Failed to create placeholder')
        }
        
    }, [
        newPlaceholderKey,
        newPlaceholderValue,
        organization,
        template,
        fetchTemplate,
        templatePlaceholders,
    ])
    
    const onAddDefaultPlaceholdersClick = useCallback(async () => {
        
        await Promise.all(templatePlaceholders.map(it => onAddPlaceholderClick(it, it)))
        
    }, [onAddPlaceholderClick, templatePlaceholders])
    
    const onConfirmDeletePlaceholderClick = useCallback(async () => {
        
        try {
            await actions.deleteExportTemplatePlaceholder(
                organization.id,
                template.id,
                pendingDeletePlaceholder.id,
            )
            await fetchTemplate()
            toast.success(`Deleted placeholder "${pendingDeletePlaceholder.key}"`)
        } catch (e) {
            console.error('onConfirmDeletePlaceholderClick', e)
            toast.error('Failed to delete placeholder')
        }
        
    }, [
        organization,
        fetchTemplate,
        pendingDeletePlaceholder,
        template,
    ])
    
    const updateTemplateFields = useCallback(async updatedFields => {
        
        setIsUpdatingTemplateFields(true)
        
        try {
            
            await actions.setExportTemplateFields(
                organization.id,
                template.id,
                updatedFields.map((it, i) => ({
                    ...it,
                    order: i,
                })),
            )
            
            await fetchTemplate()
            
            toast.success('Updated export template fields')
            
        } catch (e) {
            
            console.error('updateTemplateFields', e)
            toast.error('Failed to update fields')
            
        }
        
        setIsUpdatingTemplateFields(false)
        
    }, [organization, fetchTemplate, template])
    
    useEffect(() => { actions.fetchOrgs() }, [])
    
    useEffect(() => {
        
        if (!organization) return
        
        if (!exportTemplates?.length)
            fetchExportTemplates()
        else if (!template)
            fetchTemplate()
        
        if (!templatePlaceholders?.length)
            fetchTemplatePlaceholders()
        
    }, [
        organization,
        exportTemplates,
        templatePlaceholders,
        template,
        fetchExportTemplates,
        fetchTemplate,
        fetchTemplatePlaceholders,
    ])
    
    useEffect(() => {
        
        if (!template) return
        
        setStartingRow(template.startingRow ?? '1')
        setStartingCol(template.startingCol ?? startingColOptions[0])
        setWorkbookSheet(template.defaultSheet ?? '')
        
    }, [template])
    
    return {
        refNewPlaceholderKey,
        placeholdersInfoModalOpen,
        setPlaceholdersInfoModalOpen,
        fieldsInfoModalOpen,
        setFieldsInfoModalOpen,
        startingRowOptions,
        startingColOptions,
        
        exportTemplates,
        
        startingRow,
        setStartingRow,
        startingCol,
        setStartingCol,
        workbookSheet,
        setWorkbookSheet,
        
        templatePlaceholders,
        setTemplatePlaceholders,
        newPlaceholderKey,
        setNewPlaceholderKey,
        newPlaceholderValue,
        setNewPlaceholderValue,
        pendingDeletePlaceholder,
        setPendingDeletePlaceholder,
        pendingTemplateRename,
        setPendingTemplateRename,
        isUpdatingTemplateFields,
        setIsUpdatingTemplateFields,
        
        organizations,
        organization,
        organizationError,
        organizationIsLoading,
        fetchOrganization,
        isInternalTemplate,
        disabledProps,
        
        template,
        
        fetchExportTemplates,
        fetchTemplatePlaceholders,
        fetchTemplate,
        updateTemplateFields,
        onRenameTemplateClick,
        onStartingRowChange,
        onStartingColChange,
        onWorkbookSheetChange,
        onPlaceholderValueChange,
        onAddPlaceholderClick,
        onAddDefaultPlaceholdersClick,
        onConfirmDeletePlaceholderClick,
    }
    
}

export default useTemplateViewModel
